/**
 * IM-004: Wraps Commerce SDK `useProducts` and injects `inventoryIds` from the warehouse cookie.
 */

import {useMemo} from 'react';

import {useProducts as useBaseProducts} from '@salesforce/commerce-sdk-react';

import {
  CHECKOUT_PRODUCT_EXPAND,
} from '../utils/api-utils';
import {mergeCommerceSdkProductOptionsWithInventoryIds} from '../utils/merge-commerce-sdk-product-options';
import {useShopperInventoryIds} from './use-shopper-inventory-ids';

// Re-export for existing checkout call sites.
export {CHECKOUT_PRODUCT_EXPAND};

/**
 * @param {Object} options - Commerce SDK `useProducts` options
 * @param {Object} [queryOptions] - React Query options
 * @param {Object} [config]
 * @param {string|undefined} [config.inventoryIds] - When provided, overrides cookie-based ids
 * @param {'checkout'} [config.mode] - When `'checkout'` and no explicit `expand` is
 *   supplied, injects {@link CHECKOUT_PRODUCT_EXPAND} to shrink the payload. Inert otherwise.
 */
export function useProducts(options, queryOptions, config) {
  const cookieIds = useShopperInventoryIds();
  const inventoryIds =
    config && Object.prototype.hasOwnProperty.call(config, 'inventoryIds') ?
      config.inventoryIds :
      cookieIds;
  const mode = config?.mode;
  const scopedOptions = useMemo(() => {
    if (mode !== 'checkout' || options?.parameters?.expand) {
      return options;
    }

    return {
      ...options,
      parameters: {...options?.parameters, expand: CHECKOUT_PRODUCT_EXPAND},
    };
  }, [mode, options]);
  const mergedOptions = useMemo(
    () => mergeCommerceSdkProductOptionsWithInventoryIds(scopedOptions, inventoryIds),
    [scopedOptions, inventoryIds]
  );

  return useBaseProducts(mergedOptions, queryOptions);
}

export default useProducts;
